import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid-pro';
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import type { CustomGridColDef } from '../services';
import { hasValidHeader } from './columnFilters';
import { extractColumnFields, isStringField } from './columnHelpers';

export type ProcessedColumns<T extends GridValidRowModel> = {
  visible: (GridColDef<T> | CustomGridColDef<T>)[];
  hidden: (GridColDef<T> | CustomGridColDef<T>)[];
  cannotBeHidden: (GridColDef<T> | CustomGridColDef<T>)[];
  pinned: string[];
  defaultSort: string | undefined;
};

const isExplicitDefaultSort = <T extends GridValidRowModel>(col: GridColDef<T> | CustomGridColDef<T>): boolean =>
  'isDefaultSort' in col && col.isDefaultSort === true && hasValidHeader(col);

const canBeFallbackDefaultSort = <T extends GridValidRowModel>(col: GridColDef<T> | CustomGridColDef<T>): boolean =>
  (!('cannotBeHidden' in col) || col.cannotBeHidden !== false) &&
  hasValidHeader(col) &&
  (!('isHidden' in col) || !col.isHidden);

export const processColumns = <T extends GridValidRowModel>(
  columns: (GridColDef<T> | CustomGridColDef<T>)[],
  defaultColumns: CustomGridColDef<T>[],
  leftPaddingName?: string,
): ProcessedColumns<T> => {
  const visible: (GridColDef<T> | CustomGridColDef<T>)[] = [];
  const hidden: (GridColDef<T> | CustomGridColDef<T>)[] = [];
  const cannotBeHidden: (GridColDef<T> | CustomGridColDef<T>)[] = [];
  let defaultSortColumn: GridColDef<T> | CustomGridColDef<T> | undefined = undefined;
  let fallbackSortColumn: GridColDef<T> | CustomGridColDef<T> | undefined = undefined;

  for (const col of columns) {
    if ('isHidden' in col && col.isHidden) {
      hidden.push(col);
    } else {
      visible.push(col);
    }

    if ('cannotBeHidden' in col && col.cannotBeHidden) {
      cannotBeHidden.push(col);
    }

    if (!defaultSortColumn && isExplicitDefaultSort(col)) {
      defaultSortColumn = col;
    }

    if (!defaultSortColumn && !fallbackSortColumn && canBeFallbackDefaultSort(col)) {
      fallbackSortColumn = col;
    }
  }

  const finalDefaultSortColumn = defaultSortColumn || fallbackSortColumn;

  const pinnedFields: string[] = [];

  const cannotBeHiddenFields = extractColumnFields(
    defaultColumns.filter(
      (col) => !('isHidden' in col && col.isHidden) && 'cannotBeHidden' in col && col.cannotBeHidden,
    ),
  );
  pinnedFields.push(...cannotBeHiddenFields);

  const hasCheckbox = columns.some(
    (col) =>
      !('isHidden' in col && col.isHidden) &&
      col.field === GRID_CHECKBOX_SELECTION_FIELD &&
      !('cannotBeHidden' in col && col.cannotBeHidden),
  );
  if (hasCheckbox) {
    pinnedFields.push(GRID_CHECKBOX_SELECTION_FIELD);
  }

  const userPinnedFields = extractColumnFields(
    columns.filter(
      (col) =>
        !('isHidden' in col && col.isHidden) &&
        'pinned' in col &&
        col.pinned &&
        !('cannotBeHidden' in col && col.cannotBeHidden) &&
        col.field !== GRID_CHECKBOX_SELECTION_FIELD,
    ),
  );

  if (userPinnedFields.length > 0 && leftPaddingName) {
    pinnedFields.push(leftPaddingName, ...userPinnedFields);
  }

  return {
    visible,
    hidden,
    cannotBeHidden,
    pinned: pinnedFields,
    defaultSort:
      finalDefaultSortColumn && isStringField(finalDefaultSortColumn.field) ? finalDefaultSortColumn.field : undefined,
  };
};
